import {useCallback, useState} from "react";
import {toast} from "sonner";
import {addToCart, type CartLine, type Product} from "@corporatesclub/shared";
import {useShopSite} from "@/components/shop/ShopSiteProvider";

export type CartAddedProduct = Pick<Product, "brand" | "nume" | "imagine" | "pretEuro" | "pretLei" | "pretUnit">;

export type AddToCartInput = {
  id_produs: number;
  _pret: number;
  cantitate?: number;
  _locatie: string;
  product?: CartAddedProduct;
};

export type CartAddedDetail = {
  productId: number;
  count: number;
  message?: string;
  qtyAdded?: number;
  addedLine?: CartLine;
  cartSubtotal?: number;
  cartTotal?: number;
  eurRate?: number;
  product?: CartAddedProduct;
  upsellProducts?: Product[];
};

export function productSnapshot(from?: CartAddedProduct, line?: CartLine): CartAddedProduct | undefined {
  if(from?.nume) return from;
  const lp = line?.product;
  if(lp?.nume) {
    return {
      brand: lp.brand ?? from?.brand ?? "",
      nume: lp.nume,
      imagine: lp.imagine ?? from?.imagine ?? "",
      pretEuro: lp.pretEuro ?? from?.pretEuro ?? "",
      pretLei: lp.pretLei ?? line?.pretLei ?? from?.pretLei ?? "",
      pretUnit: lp.pretUnit ?? line?.unitPrice ?? from?.pretUnit ?? 0
    };
  }
  if(from) return from;
  if(!line) return undefined;
  return {
    brand: "",
    nume: "",
    imagine: "",
    pretEuro: "",
    pretLei: line.pretLei ?? "",
    pretUnit: line.unitPrice ?? 0
  };
}

export function dispatchCartAdded(productId: number, r: Awaited<ReturnType<typeof addToCart>>, product?: CartAddedProduct) {
  const snapshot = productSnapshot(product, r.addedLine);
  window.dispatchEvent(new CustomEvent("cart-updated", {detail: r.count}));
  window.dispatchEvent(new CustomEvent("cart-added", {detail: {
    productId,
    count: r.count,
    message: r.message,
    qtyAdded: r.qtyAdded ?? 1,
    addedLine: r.addedLine,
    cartSubtotal: r.cartSubtotal,
    cartTotal: r.cartTotal,
    eurRate: r.eurRate,
    product: snapshot,
    upsellProducts: r.upsellProducts
  } satisfies CartAddedDetail}));
}

export function useAddToCart() {
  const {status, ready} = useShopSite();
  const canAddToCart = !ready || status.shopEnabled;
  const closedMessage = status.closedMessage;
  const [addingId, setAddingId] = useState<number | null>(null);
  const add = useCallback(async (input: AddToCartInput) => {
    if(ready && !status.shopEnabled) {
      toast.warning(status.closedMessage);
      throw new Error(status.closedMessage);
    }
    setAddingId(input.id_produs);
    try {
      const r = await addToCart({
        id_produs: input.id_produs,
        _pret: input._pret,
        cantitate: input.cantitate ?? 1,
        _locatie: input._locatie
      });
      dispatchCartAdded(input.id_produs, r, input.product);
      return r;
    } finally {
      setAddingId(null);
    }
  }, [ready, status.closedMessage, status.shopEnabled]);
  function isAdding(id: number) {
    return addingId === id;
  }
  return {add, isAdding, addingId, canAddToCart, closedMessage};
}

export function addProductToCart(product: Product, cantitate = 1) {
  return addToCart({
    id_produs: product.id,
    _pret: product.pretUnit,
    cantitate,
    _locatie: product.locatie
  }).then((r) => {
    dispatchCartAdded(product.id, r, product);
    return r;
  });
}
